import React, { useEffect, useState } from "react";
import { Row, Col, Button, FormGroup } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import { LabelText } from "../Utils/Input";
import InputField from "../Utils/InputField";
import SelectField from "../Utils/SelectField";
import CommonButton from "../Utils/CommonButton";
import { CreatableSelectField } from "../Utils/CreatableSelectField";
import { Loader } from "../Utils/Loader";

import { fetchAllUom } from "../../slices/uom/thunk";
import { getAllCategory } from "../../slices/Category/GetCategory/thunk";
import { createItem } from "../../slices/Item/AddItem/thunk";
import { updateItem } from "../../slices/Item/EditItem/thunk";
import { getCatAttribute } from "../../slices/Item/GetCatAttribute/thunk";
import { getCatBrand } from "../../slices/Item/GetCatBrand/thunk";

function ItemsForm({ closeModal, onSuccess, selectedItem }) {
  const dispatch = useDispatch();
  const isEditMode = !!selectedItem;
  const [loading, setLoading] = useState(false);
  const [apiloading, apisetLoading] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);

  const uomList = useSelector((state) => state.Uom.uom);
  const categoryList = useSelector((state) => state.Category.categories || []);
  const catAttributeList = useSelector(
    (state) => state.GetCatAttribute.catAttributes || [],
  );
  const catBrandList = useSelector(
    (state) => state.GetCatBrand.catBrands || [],
  );

  useEffect(() => {
    dispatch(fetchAllUom(apisetLoading));
    dispatch(getAllCategory(apisetLoading));
  }, [dispatch]);

  const uomOptions = uomList?.map((item) => ({
    label: item?.name,
    value: item?.name,
  }));

  const categoryOptions = categoryList?.map((item) => ({
    label: item?.name,
    value: item?.categoryid,
    data: item,
  }));

  const attributeOptions = Array.isArray(catAttributeList)
    ? catAttributeList.map((item) => ({
        label: item?.name,
        value: item?.name,
        data: item,
      }))
    : [];

  const brandOptions = catBrandList?.map((item) => ({
    label: item.charAt(0).toUpperCase() + item.slice(1),
    value: item,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      itemName: selectedItem?.itemname || "",
      sku: selectedItem?.sku || "",
      stockThreshold: selectedItem?.stockthreshold || "",
      category: null,
      brand: selectedItem?.brand
        ? {
            label:
              selectedItem?.brand.charAt(0).toUpperCase() +
              selectedItem?.brand.slice(1),
            value: selectedItem?.brand,
          }
        : null,
      uom: selectedItem?.uom
        ? {
            label: selectedItem?.uom,
            value: selectedItem?.uom,
          }
        : null,
      description: selectedItem?.description || "",
      attributes:
        selectedItem?.properties?.length > 0
          ? selectedItem.properties.map((prop) => ({
              name: {
                label: prop?.attributes?.name,
                value: prop?.attributes?.name,
              },
              value: prop?.attributes?.value,
            }))
          : [
              {
                name: null,
                value: "",
              },
            ],
    },
    validationSchema: Yup.object({
      itemName: Yup.string().required("Required"),
      sku: Yup.string().required("Required"),
      stockThreshold: Yup.number().required("Required"),
      category: Yup.object().required("Required"),
      brand: Yup.object().required("Required"),
      uom: Yup.object().required("Required"),
    }),

    onSubmit: (values) => {
      const categoryPath = [
        values.category?.label,

        ...dynamicFields
          .filter((field) => field.selected)
          .map((field) => field.selected?.label),
      ];

      const payload = {
        itemname: values.itemName,
        brand: values.brand?.value,
        sku: values.sku,
        stockthreshold: Number(values.stockThreshold) || 0,
        categoryPath,
        uom: values.uom?.label,
        properties: values.attributes.map((attr) => ({
          attributes: {
            name: attr?.name?.label,
            value: attr?.value,
            isUnique: true,
          },
        })),
        description: values.description,
        isActive: true,
      };

      if (isEditMode) {
        dispatch(
          updateItem(payload, selectedItem?.itemid, setLoading, () => {
            onSuccess?.();
            closeModal?.();
          }),
        );
      } else {
        dispatch(
          createItem(payload, setLoading, () => {
            onSuccess?.();
            closeModal?.();
          }),
        );
      }
    },
  });

  const { values, errors, touched, handleChange, setFieldValue, handleSubmit } =
    formik;

  useEffect(() => {
    if (
      selectedItem &&
      categoryList?.length > 0 &&
      selectedItem?.categorypath?.length > 0
    ) {
      const path = selectedItem.categorypath;

      const mainCategory = categoryList.find((cat) => cat?.name === path[0]);

      if (!mainCategory) return;

      const mainCategoryOption = {
        label: mainCategory.name,
        value: mainCategory.categoryid,
        data: mainCategory,
      };

      dispatch(getCatAttribute(mainCategory.name, setLoading));
      dispatch(getCatBrand(mainCategory.name, setLoading));

      setFieldValue("category", mainCategoryOption);

      let dynamicData = [];
      let currentSubcategory = mainCategory?.subcategory;

      const fieldLabels = [
        "Sub Category",
        "Type",
        "Variant",
        "Model",
        "Option",
      ];

      for (let i = 1; i < path.length; i++) {
        const currentName = path[i];

        const options = Object.values(currentSubcategory || {}).map((item) => ({
          label: item?.name,
          value: item?.categoryid,
          data: item,
        }));

        const selectedOption = options.find(
          (opt) => opt?.label === currentName,
        );

        dynamicData.push({
          label: fieldLabels[i - 1] || `Level ${i}`,
          options,
          selected: selectedOption || null,
        });

        currentSubcategory = selectedOption?.data?.subcategory;
      }

      setDynamicFields(dynamicData);
    }
  }, [selectedItem, categoryList]);

  useEffect(() => {
    if (
      isEditMode &&
      selectedItem?.properties?.length > 0 &&
      catAttributeList?.length > 0
    ) {
      const updatedAttributes = selectedItem.properties.map((prop) => ({
        name:
          attributeOptions.find(
            (opt) => opt.label === prop?.attributes?.name,
          ) || null,
        value: prop?.attributes?.value || "",
      }));

      setFieldValue("attributes", updatedAttributes);
    }
  }, [catAttributeList]);

  // CATEGORY CHANGE
  const handleCategoryChange = (selected) => {
    setFieldValue("category", selected);
    setDynamicFields([]);

    setFieldValue("attributes", [
      {
        name: null,
        value: "",
      },
    ]);

    dispatch(getCatAttribute(selected?.label, setLoading));
    dispatch(getCatBrand(selected?.label, setLoading));

    const categoryData = categoryList.find(
      (item) => item.categoryid === selected?.value,
    );

    const subcategories = categoryData?.subcategory || {};

    const options = Object.values(subcategories).map((item) => ({
      label: item?.name,
      value: item?.categoryid,
      data: item,
    }));

    if (options.length > 0) {
      setDynamicFields([
        {
          label: "Sub Category",
          options,
          selected: null,
        },
      ]);
    }
  };

  // DYNAMIC FIELD CHANGE
  const handleDynamicChange = (selected, fieldIndex) => {
    const updatedFields = [...dynamicFields];

    updatedFields[fieldIndex].selected = selected;
    updatedFields.splice(fieldIndex + 1);

    const nextData = selected?.data?.subcategory;

    if (nextData) {
      const nextOptions = Object.values(nextData).map((item) => ({
        label: item?.name,
        value: item?.categoryid,
        data: item,
      }));

      const fieldLabels = [
        "Sub Category",
        "Type",
        "Variant",
        "Model",
        "Option",
      ];

      updatedFields.push({
        label: fieldLabels[fieldIndex + 1] || `Level ${fieldIndex + 2}`,
        options: nextOptions,
        selected: null,
      });
    }

    setDynamicFields(updatedFields);
  };

  // ADD ATTRIBUTE
  const addAttribute = () => {
    setFieldValue("attributes", [
      ...values.attributes,
      {
        name: null,
        value: "",
      },
    ]);
  };

  const handleAttrChange = (index, field, value) => {
    const updated = [...values.attributes];

    if (field === "name") {
      updated[index].name = value;
      updated[index].value = "";
    } else {
      updated[index][field] = value;
    }

    setFieldValue("attributes", updated);
  };

  const getFilteredAttributeOptions = (currentIndex) => {
    const selectedValues = values.attributes
      .map((item, index) => (index !== currentIndex ? item?.name?.value : null))
      .filter(Boolean);

    return attributeOptions.filter(
      (option) => !selectedValues.includes(option.value),
    );
  };

  // REMOVE ATTRIBUTE
  const removeAttribute = (index) => {
    if (values.attributes.length === 1) return;

    const updated = values.attributes.filter((_, i) => i !== index);
    setFieldValue("attributes", updated);
  };

  return (
    <>
      {loading && <Loader />}
      <form onSubmit={handleSubmit}>
        <Row>
          {/* ITEM NAME */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                htmlFor={"type"}
                className={"text-danger"}
                displayText={"Product Name"}
                important={"*"}
              />

              <InputField
                name="itemName"
                placeholder="Enter product name"
                value={values.itemName}
                handleChange={handleChange}
                invalid={touched.itemName && errors.itemName}
              />
            </FormGroup>
          </Col>

          {/* SKU */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                htmlFor={"sku"}
                className={"text-danger"}
                displayText={"SKU"}
                important={"*"}
              />

              <InputField
                name="sku"
                placeholder="Enter SKU"
                value={values.sku}
                handleChange={handleChange}
                invalid={touched.sku && errors.sku}
              />
            </FormGroup>
          </Col>

          {/* CATEGORY */}
          <Col md={12}>
            <FormGroup>
              <LabelText
                htmlFor={"category"}
                className={"text-danger"}
                displayText={"Category"}
                important={"*"}
              />

              <SelectField
                value={values.category}
                placeholder="Select category"
                options={categoryOptions}
                handleChange={handleCategoryChange}
                isRVStyle={true}
                className={
                  touched.category && errors.category ? "error-input" : ""
                }
              />
            </FormGroup>
          </Col>

          {/* DYNAMIC CATEGORY LEVELS */}
          {dynamicFields.map((field, index) => (
            <Col md={12} key={index}>
              <FormGroup>
                <LabelText
                  htmlFor={field.label}
                  className={"text-danger"}
                  displayText={field.label}
                  important={"*"}
                />

                <SelectField
                  value={field.selected}
                  placeholder={`Select ${field.label}`}
                  options={field.options}
                  handleChange={(val) => handleDynamicChange(val, index)}
                  isRVStyle={true}
                />
              </FormGroup>
            </Col>
          ))}

          {/* BRAND */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                htmlFor={"brand"}
                className={"text-danger"}
                displayText={"Brand Name"}
                important={"*"}
              />

              <CreatableSelectField
                name="brand"
                values={values.brand}
                className={errors.brand ? "error-input" : ""}
                handleChange={(val) => {
                  setFieldValue("brand", val);
                }}
                options={brandOptions || []}
                placeholder="Select Brand"
                isClearable={true}
                isRVStyle={true}
              />
            </FormGroup>
          </Col>

          {/* UOM */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                htmlFor={"unit"}
                className={"text-danger"}
                displayText={"Unit"}
                important={"*"}
              />

              <SelectField
                className={errors.uom ? "error-input" : ""}
                value={values.uom}
                placeholder="Select Unit"
                options={uomOptions}
                handleChange={(val) => setFieldValue("uom", val)}
                isClearable={true}
                isRVStyle={true}
              />
            </FormGroup>
          </Col>

          {/* STOCK THRESHOLD */}
          <Col md={6}>
            <FormGroup>
              <LabelText
                displayText={"Stock Threshold"}
                className={"text-danger"}
                placeholder="Enter Stock Threshold"
                important={"*"}
              />

              <InputField
                name="stockThreshold"
                type="text"
                value={values.stockThreshold}
                handleChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setFieldValue("stockThreshold", value);
                }}
                invalid={touched.stockThreshold && errors.stockThreshold}
              />
            </FormGroup>
          </Col>

          {/* PROPERTIES */}
          <Col md={12}>
            <div className="border rounded-3 p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-semibold">
                  Properties
                  <span className="ms-2 badge bg-light text-dark">
                    {values.attributes.length}
                  </span>
                </div>

                <Button
                  type="button"
                  className="rv-primary-btn px-3 rounded-1 py-0 d-flex align-items-center"
                  style={{
                    fontSize: "13px",
                    minWidth: "70px",
                    height: "30px",
                    lineHeight: "1",
                  }}
                  onClick={addAttribute}
                >
                  <i className="ri-add-line me-1"></i>
                  Add
                </Button>
              </div>

              <Row className="mb-3 fw-semibold d-none d-md-flex">
                <Col className="flex-grow-1">Attribute</Col>
                <Col className="flex-grow-1">Value</Col>
                <Col xs="auto"></Col>
              </Row>

              {values.attributes.map((attr, i) => (
                <Row key={i} className="g-2 align-items-center mb-3">
                  {/* Attribute */}
                  <Col xs={12} md className="flex-grow-1">
                    <label className="d-md-none fw-semibold mb-1">
                      Attribute
                    </label>

                    <SelectField
                      value={attr.name}
                      placeholder="Select Attribute"
                      options={getFilteredAttributeOptions(i)}
                      handleChange={(val) => handleAttrChange(i, "name", val)}
                      isRVStyle={true}
                    />
                  </Col>

                  {/* Value */}
                  <Col xs={12} md className="flex-grow-1">
                    <label className="d-md-none fw-semibold mb-1">Value</label>

                    <input
                      className="form-control"
                      placeholder="Enter Value"
                      value={attr.value}
                      onChange={(e) =>
                        handleAttrChange(i, "value", e.target.value)
                      }
                    />
                  </Col>

                  {/* Delete Button */}
                  <Col xs="auto" className="text-center">
                    <i
                      className="ri-delete-bin-5-line text-muted"
                      style={{
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                      onClick={() => removeAttribute(i)}
                    />
                  </Col>
                </Row>
              ))}
            </div>
          </Col>

          {/* DESCRIPTION */}
          <Col md={12} className="mt-2">
            <FormGroup>
              <label className="fw-semibold">Description</label>

              <textarea
                className="form-control"
                rows="4"
                name="description"
                placeholder="Enter item description..."
                value={values.description}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>

          {/* BUTTONS */}
          <div
            className="d-flex justify-content-center w-100 gap-2 flex-wrap pe-1 py-3"
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#fff",
              borderTop: "1px solid #e9ebec",
              marginTop: "10px",
            }}
          >
            <div className="d-flex gap-2 justify-content-center">
              <CommonButton
                outline={true}
                className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                color={"main-color"}
                type={"button"}
                buttonAction={() => closeModal()}
                buttonText={"Cancel"}
                icon={
                  <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                }
                btnTxtClsName="px-2 ps-3 ms-2 me-1 fw-bold"
              />

              <CommonButton
                color={"main-color"}
                className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                icon={
                  <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-2"></i>
                }
                btnTxtClsName="px-2 ps-3 ms-2 me-1 fw-bold"
                type={"submit"}
                buttonText={"Submit"}
              />
            </div>
          </div>
        </Row>
      </form>
    </>
  );
}

export default ItemsForm;
